// MorrowOS file VAULT client helper.
//
// Talks to the owner-scoped server routes:
//   GET    /api/morrow/vault                → list the caller's items (metadata)
//   POST   /api/morrow/vault                → upload a file (base64 data URL)
//   GET    /api/morrow/vault/:id/content    → the item's bytes (preview / attach)
//   DELETE /api/morrow/vault/:id            → remove an item
//
// The caller's owner id + Clerk token are attached automatically by the global
// fetch patch (installApiFetchAuth) for any request routed through apiUrl(), so
// these calls stay owner-scoped without passing ids by hand. Calls never throw —
// failures resolve to empty/false so the UI keeps rendering.

import { apiUrl } from "./apiBase";
import { openUpgradeModal } from "./upgradeModal";

export interface VaultItem {
  readonly id: string;
  readonly name: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
  readonly sensitivity: string;
  /** True when this item has extracted document text (a "view text" affordance). */
  readonly hasText: boolean;
  /** How many structured items were extracted from this file (search/image index). */
  readonly itemCount: number;
  /** The folder this file is filed in, or null for unfiled. */
  readonly folderId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

function asVaultItem(value: unknown): VaultItem | null {
  if (!value || typeof value !== "object") return null;
  const rec = value as Record<string, unknown>;
  if (typeof rec.id !== "string" || typeof rec.name !== "string") return null;
  return {
    id: rec.id,
    name: rec.name,
    mimeType: typeof rec.mimeType === "string" ? rec.mimeType : "application/octet-stream",
    sizeBytes: typeof rec.sizeBytes === "number" ? rec.sizeBytes : 0,
    sensitivity: typeof rec.sensitivity === "string" ? rec.sensitivity : "normal",
    hasText: rec.hasText === true,
    itemCount: typeof rec.itemCount === "number" ? rec.itemCount : 0,
    folderId: typeof rec.folderId === "string" && rec.folderId ? rec.folderId : null,
    createdAt: typeof rec.createdAt === "string" ? rec.createdAt : "",
    updatedAt: typeof rec.updatedAt === "string" ? rec.updatedAt : "",
  };
}

/** Owner-scoped URL that streams an item's bytes (for <img> previews / fetch). */
export function vaultItemContentUrl(id: string): string {
  return apiUrl(`/api/morrow/vault/${encodeURIComponent(id)}/content`);
}

/**
 * Extract the vault item id from a vault URL, or null if the URL is not one.
 * Recognizes BOTH forms an agent-generated artifact can carry:
 *  - the owner-scoped content URL `/api/morrow/vault/<id>/content` (private read)
 *  - the public tokenized URL `/api/morrow/vault/public/<id>?token=...` minted by
 *    the server for generated images/PDFs (this is the URL generateArtifact
 *    returns, so it MUST match for click-to-open to fire)
 * Both relative and api-origin-prefixed absolute forms are accepted.
 */
const VAULT_CONTENT_PATH_PATTERN = /\/api\/morrow\/vault\/([^/?#]+)\/content(?:$|[/?#])/;
const VAULT_PUBLIC_PATH_PATTERN = /\/api\/morrow\/vault\/public\/([^/?#]+)(?:$|[/?#])/;
export function vaultItemIdFromUrl(url: string): string | null {
  const match = VAULT_CONTENT_PATH_PATTERN.exec(url) ?? VAULT_PUBLIC_PATH_PATTERN.exec(url);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function inferVaultArtifactMimeType(
  url: string,
  name: string,
  artifactKind?: "image" | "pdf" | "video",
): string {
  const haystack = `${url} ${name}`.toLowerCase();
  if (/\.pdf(?:$|[?#])/.test(haystack)) return "application/pdf";
  if (/\.png(?:$|[?#])/.test(haystack)) return "image/png";
  if (/\.jpe?g(?:$|[?#])/.test(haystack)) return "image/jpeg";
  if (/\.gif(?:$|[?#])/.test(haystack)) return "image/gif";
  if (/\.webp(?:$|[?#])/.test(haystack)) return "image/webp";
  if (/\.svg(?:$|[?#])/.test(haystack)) return "image/svg+xml";
  if (/\.(?:heic|heif)(?:$|[?#])/.test(haystack)) return "image/heic";
  if (/\.mp4(?:$|[?#])/.test(haystack)) return "video/mp4";
  if (/\.webm(?:$|[?#])/.test(haystack)) return "video/webm";
  if (/\.mov(?:$|[?#])/.test(haystack)) return "video/quicktime";
  if (/\.m4v(?:$|[?#])/.test(haystack)) return "video/x-m4v";
  // Generated AUDIO (generate_audio / voice artifacts) had no entry here, so it
  // fell through to application/octet-stream and the preview rendered it with
  // the catch-all <img> branch: a dead element instead of a player.
  if (/\.mp3(?:$|[?#])/.test(haystack)) return "audio/mpeg";
  if (/\.wav(?:$|[?#])/.test(haystack)) return "audio/wav";
  if (/\.(?:m4a|m4b)(?:$|[?#])/.test(haystack)) return "audio/mp4";
  if (/\.aac(?:$|[?#])/.test(haystack)) return "audio/aac";
  if (/\.flac(?:$|[?#])/.test(haystack)) return "audio/flac";
  if (/\.(?:ogg|oga)(?:$|[?#])/.test(haystack)) return "audio/ogg";
  if (/\.weba(?:$|[?#])/.test(haystack)) return "audio/webm";
  if (artifactKind === "pdf") return "application/pdf";
  if (artifactKind === "video") return "video/mp4";
  if (artifactKind === "image") return "image/png";
  return "application/octet-stream";
}

/** Build minimal vault metadata from an agent-generated artifact URL in chat. */
export function vaultItemFromArtifactLink(
  url: string,
  title?: string,
  artifactKind?: "image" | "pdf" | "video",
): VaultItem | null {
  const id = vaultItemIdFromUrl(url);
  if (!id) return null;
  const name = title?.trim() || "Document";
  return {
    id,
    name,
    mimeType: inferVaultArtifactMimeType(url, name, artifactKind),
    sizeBytes: 0,
    sensitivity: "normal",
    hasText: false,
    // An artifact link carries no folder — it is a chat reference, not a filed vault row.
    folderId: null,
    itemCount: 0,
    createdAt: "",
    updatedAt: "",
  };
}

/** List the caller's vault items (newest first). Returns [] on any failure. */
export async function fetchVaultItems(folderId?: string | null): Promise<VaultItem[]> {
  try {
    // "none" asks for the unfiled ones; omitted asks for everything.
    const query =
      folderId === undefined ? "" : `?folderId=${encodeURIComponent(folderId ?? "none")}`;
    const res = await fetch(apiUrl(`/api/morrow/vault${query}`), {
      headers: { accept: "application/json" },
    });
    if (!res.ok) return [];
    const body = (await res.json()) as { items?: unknown } | null;
    const items = body && Array.isArray(body.items) ? body.items : [];
    return items.map(asVaultItem).filter((item): item is VaultItem => item !== null);
  } catch (err) {
    console.warn("[vault] list error", err);
    return [];
  }
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(file);
  });
}

/**
 * Files above this go through the presigned direct-to-R2 path (browser → R2, no
 * size wall). Smaller ones ride the base64 route, which also works on desktop /
 * local where R2 presigning isn't configured. Kept safely under the server's
 * base64 cap (10 MB) so the base64 path never rejects a "small" file.
 */
const PRESIGN_THRESHOLD_BYTES = 8 * 1024 * 1024;

export interface UploadToVaultOptions {
  /** Store into the caller's active org SHARED vault instead of personal. */
  readonly scope?: "org";
  /** Upload progress for large (direct-to-R2) uploads: fraction 0..1. */
  readonly onProgress?: (fraction: number) => void;
  /** File it straight into this folder. Travels WITH the upload, so dropping onto a folder
   * cannot half-succeed as "uploaded but unfiled". */
  readonly folderId?: string | null;
}

/** Base64 JSON upload — the small-file path (works everywhere incl. desktop). */
async function uploadViaBase64(
  file: File,
  scope?: "org",
  folderId?: string | null,
): Promise<VaultItem | null> {
  const dataUrl = await readFileAsDataUrl(file);
  const res = await fetch(apiUrl("/api/morrow/vault"), {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({
      name: file.name,
      mimeType: file.type,
      dataUrl,
      ...(scope ? { scope } : {}),
      ...(folderId ? { folderId } : {}),
    }),
  });
  // TRIAL: out of vault room → surface the "basket too small" upgrade modal
  // instead of a silent null, so the user sees why the file didn't land.
  if (res.status === 402) {
    openUpgradeModal({ capability: "vault.storage.mb" });
    return null;
  }
  if (!res.ok) return null;
  const body = (await res.json()) as { item?: unknown } | null;
  return body ? asVaultItem(body.item) : null;
}

/** PUT bytes straight to a presigned R2 URL via XHR (fetch has no upload progress).
 * XHR is deliberate: it bypasses the global apiUrl fetch-auth patch, so no Clerk
 * token is ever sent to the R2 host. Resolves true on a 2xx. */
function putBytesToR2(
  url: string,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url, true);
      if (file.type) xhr.setRequestHeader("content-type", file.type);
      xhr.upload.onprogress = (e) => {
        if (onProgress && e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
      };
      xhr.onload = () => resolve(xhr.status >= 200 && xhr.status < 300);
      xhr.onerror = () => resolve(false);
      xhr.onabort = () => resolve(false);
      xhr.send(file);
    } catch {
      resolve(false);
    }
  });
}

/** Presigned direct-to-R2 upload — the large-file path. Returns "unconfigured"
 * when the server has no R2 presign creds (desktop/local) so the caller can fall
 * back; otherwise the stored item or null on failure. */
async function uploadViaPresign(
  file: File,
  scope?: "org",
  onProgress?: (fraction: number) => void,
  folderId?: string | null,
): Promise<VaultItem | "unconfigured" | null> {
  // 1. Mint a presigned PUT URL.
  const presignRes = await fetch(apiUrl("/api/morrow/vault/presign"), {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({
      name: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
      ...(scope ? { scope } : {}),
    }),
  });
  if (presignRes.status === 503) return "unconfigured";
  if (presignRes.status === 402) {
    openUpgradeModal({ capability: "vault.storage.mb" });
    return null;
  }
  if (!presignRes.ok) return null;
  const pre = (await presignRes.json().catch(() => null)) as {
    itemId?: string;
    uploadUrl?: string;
  } | null;
  if (!pre?.itemId || !pre?.uploadUrl) return null;

  // 2. Upload the bytes directly to R2 (no size wall, with progress).
  const ok = await putBytesToR2(pre.uploadUrl, file, onProgress);
  if (!ok) return null;

  // 3. Finalize — record the metadata row + fire knowledge indexing.
  const finRes = await fetch(apiUrl("/api/morrow/vault/finalize"), {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({
      itemId: pre.itemId,
      name: file.name,
      mimeType: file.type,
      ...(scope ? { scope } : {}),
      ...(folderId ? { folderId } : {}),
    }),
  });
  if (finRes.status === 402) {
    openUpgradeModal({ capability: "vault.storage.mb" });
    return null;
  }
  if (!finRes.ok) return null;
  const body = (await finRes.json().catch(() => null)) as { item?: unknown } | null;
  return body ? asVaultItem(body.item) : null;
}

/**
 * Upload a file to the vault. Large files (> ~8 MB) go direct-to-R2 via a
 * presigned PUT — no size wall, so 100 MB documents/images work; smaller files use
 * the base64 route (also the fallback when direct upload isn't configured, e.g.
 * desktop). Returns the stored item metadata, or null.
 */
export async function uploadToVault(
  file: File,
  options?: UploadToVaultOptions,
): Promise<VaultItem | null> {
  try {
    const scope = options?.scope;
    const folderId = options?.folderId ?? null;
    if (file.size > PRESIGN_THRESHOLD_BYTES) {
      const viaPresign = await uploadViaPresign(file, scope, options?.onProgress, folderId);
      if (viaPresign !== "unconfigured") return viaPresign;
      // Direct upload unavailable (desktop/local): fall through to base64. The
      // server will 413 if it's genuinely too big for that path.
    }
    return await uploadViaBase64(file, scope, folderId);
  } catch (err) {
    console.warn("[vault] upload error", err);
    return null;
  }
}

/**
 * Fetch a vault item's bytes and wrap them as a `File`, ready to stage into the
 * composer exactly like a device upload. Returns null if the bytes are missing.
 */
export async function fetchVaultItemAsFile(item: VaultItem): Promise<File | null> {
  try {
    const res = await fetch(vaultItemContentUrl(item.id), { headers: { accept: "*/*" } });
    if (!res.ok) {
      console.warn("[vault] content fetch failed", item.id, res.status);
      return null;
    }
    const blob = await res.blob();
    const blobType = blob.type?.trim().toLowerCase() ?? "";
    const itemType = item.mimeType?.trim().toLowerCase() ?? "";
    const type =
      blobType && blobType !== "application/octet-stream"
        ? blob.type
        : itemType && itemType !== "application/octet-stream"
          ? item.mimeType
          : blob.type || item.mimeType || "application/octet-stream";
    return new File([blob], item.name || "file", { type });
  } catch (err) {
    console.warn("[vault] content fetch error", err);
    return null;
  }
}

/**
 * Open a vault item in a new browser tab (images, PDFs, text, etc.). Fetches
 * the owner-scoped content bytes, wraps them as a blob URL, and hands that to
 * `window.open`. Falls back to download when the popup is blocked.
 */
export async function openVaultItem(item: VaultItem): Promise<boolean> {
  try {
    const file = await fetchVaultItemAsFile(item);
    if (!file) return false;
    const url = URL.createObjectURL(file);
    const opened = window.open(url, "_blank", "noopener,noreferrer");
    if (!opened) {
      URL.revokeObjectURL(url);
      return downloadVaultItem(item);
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return true;
  } catch (err) {
    console.warn("[vault] open error", err);
    return false;
  }
}

/**
 * Download a vault item to the user's device under its original name. Fetches
 * the owner-scoped content bytes (via the same auth'd fetch as previews), wraps
 * them as a `File`, and triggers a browser download through a temporary
 * `<a download>` element. Returns true on success, false on any failure.
 */
export async function downloadVaultItem(item: VaultItem): Promise<boolean> {
  try {
    const file = await fetchVaultItemAsFile(item);
    if (!file) return false;
    const url = URL.createObjectURL(file);
    try {
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = item.name || file.name || "file";
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
    return true;
  } catch (err) {
    console.warn("[vault] download error", err);
    return false;
  }
}

/**
 * Fetch a vault item's bytes and return a short-lived object URL for authenticated
 * preview (img/video/audio/embed). The owner-scoped content route needs the auth'd
 * fetch, so a raw `vaultItemContentUrl` can't be used directly in some embed hosts
 * (e.g. Electron). Callers own the returned URL and must `URL.revokeObjectURL` it.
 */
export async function fetchVaultItemPreviewUrl(item: VaultItem): Promise<string | null> {
  try {
    const file = await fetchVaultItemAsFile(item);
    if (!file) return null;
    return URL.createObjectURL(file);
  } catch (err) {
    console.warn("[vault] preview url error", err);
    return null;
  }
}

/** Fetch a document item's extracted text (markdown/plain). Null if none. */
export async function fetchVaultItemText(id: string): Promise<string | null> {
  try {
    const res = await fetch(apiUrl(`/api/morrow/vault/${encodeURIComponent(id)}/text`), {
      headers: { accept: "application/json" },
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { text?: unknown } | null;
    return body && typeof body.text === "string" ? body.text : null;
  } catch (err) {
    console.warn("[vault] text fetch error", err);
    return null;
  }
}

/** Delete a vault item. Returns true on success. */
/**
 * Re-run knowledge ingest + item extraction over already-uploaded files, so they
 * become searchable/answerable and their items get indexed (extraction otherwise
 * only runs on new uploads). Pass an `id` to reindex one file, or nothing for all
 * of the caller's files. Returns how many files were queued (-1 on failure).
 * Processing is async server-side; results appear once each file finishes.
 */
export async function reindexVault(id?: string): Promise<number> {
  try {
    const res = await fetch(
      apiUrl(`/api/morrow/vault/reindex${id ? `?id=${encodeURIComponent(id)}` : ""}`),
      { method: "POST", headers: { accept: "application/json" } },
    );
    if (!res.ok) {
      console.warn("[vault] reindex failed", res.status);
      return -1;
    }
    const body = (await res.json().catch(() => null)) as { queued?: unknown } | null;
    return typeof body?.queued === "number" ? body.queued : 0;
  } catch (err) {
    console.warn("[vault] reindex error", err);
    return -1;
  }
}

export async function deleteVaultItem(id: string): Promise<boolean> {
  try {
    const res = await fetch(apiUrl(`/api/morrow/vault/${encodeURIComponent(id)}`), {
      method: "DELETE",
      headers: { accept: "application/json" },
    });
    if (!res.ok) {
      console.warn("[vault] delete failed", id, res.status);
      return false;
    }
    const body = (await res.json().catch(() => null)) as { ok?: unknown } | null;
    return body?.ok === true;
  } catch (err) {
    console.warn("[vault] delete error", err);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Folders
// ---------------------------------------------------------------------------

export interface VaultFolder {
  readonly id: string;
  readonly name: string;
  readonly parentId: string | null;
  /** How many of your files are filed in it. */
  readonly itemCount: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

function asVaultFolder(value: unknown): VaultFolder | null {
  if (!value || typeof value !== "object") return null;
  const rec = value as Record<string, unknown>;
  if (typeof rec.id !== "string" || typeof rec.name !== "string") return null;
  return {
    id: rec.id,
    name: rec.name,
    parentId: typeof rec.parentId === "string" && rec.parentId ? rec.parentId : null,
    itemCount: typeof rec.itemCount === "number" ? rec.itemCount : 0,
    createdAt: typeof rec.createdAt === "string" ? rec.createdAt : "",
    updatedAt: typeof rec.updatedAt === "string" ? rec.updatedAt : "",
  };
}

/** Your folders. Empty on any failure — the vault still lists files without them. */
export async function fetchVaultFolders(): Promise<VaultFolder[]> {
  try {
    const res = await fetch(apiUrl("/api/morrow/vault/folders"), {
      headers: { accept: "application/json" },
    });
    if (!res.ok) return [];
    const body = (await res.json()) as { folders?: unknown } | null;
    const folders = body && Array.isArray(body.folders) ? body.folders : [];
    return folders.map(asVaultFolder).filter((f): f is VaultFolder => f !== null);
  } catch (err) {
    console.warn("[vault] folder list error", err);
    return [];
  }
}

export async function createVaultFolder(name: string): Promise<VaultFolder | null> {
  try {
    const res = await fetch(apiUrl("/api/morrow/vault/folders"), {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ name }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { folder?: unknown } | null;
    return asVaultFolder(body?.folder);
  } catch (err) {
    console.warn("[vault] folder create error", err);
    return null;
  }
}

export async function renameVaultFolder(id: string, name: string): Promise<boolean> {
  return postOk("/api/morrow/vault/folders/rename", { id, name });
}

/**
 * Delete a folder. By default its files are UNFILED (kept, returned to unfiled); pass
 * `deleteContents: true` to delete the files inside it too (bytes and all).
 */
export async function deleteVaultFolder(
  id: string,
  options?: { readonly deleteContents?: boolean },
): Promise<boolean> {
  return postOk("/api/morrow/vault/folders/delete", {
    id,
    ...(options?.deleteContents ? { deleteContents: true } : {}),
  });
}

/** File an item into a folder, or pass null to unfile it. */
export async function moveVaultItemToFolder(
  itemId: string,
  folderId: string | null,
): Promise<boolean> {
  return postOk("/api/morrow/vault/move", { itemId, folderId });
}

async function postOk(path: string, body: unknown): Promise<boolean> {
  try {
    const res = await fetch(apiUrl(path), {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify(body),
    });
    return res.ok;
  } catch (err) {
    console.warn(`[vault] ${path} error`, err);
    return false;
  }
}
